import ee from '../../services/earth-engine';

/**
 * Suspended particulate matter from the NIR-RGB algorithm of Wei et al. (2021).
 * Transposed from the reference Earth Engine script (Mascagni, 2026, section 2.8), which is itself
 * the corrected v2 of the formulation: the version CASSIE carried before was reported as wrong in
 * shallow water and in very turbid water, and this file exists to replace it rather than to sit
 * beside it.
 * What the correction changes, in the author's own listing:
 * - the blend weight is `2500 * (Rrs671 - 0.0008)` over remote-sensing reflectance `Rrs = rho/pi`,
 *   clamped to [0, 1]; the old one was `2.5 * (rho - 0.0008)` over reflectance;
 * - the clear-water branch is the ratio 560/443 nm; the old one used 482/443 on Landsat 8 and
 *   560/482 on Landsat 5;
 * - the turbid branch uses 665, 740 and 865 nm, with 740 nm synthesised because Landsat has no such
 *   band; the old one put NIR in the 740 slot and SWIR1 in the 865 slot.
 * Band wavelengths are read through the mission's own band map, so the same code serves whichever
 * Landsat collection is loaded. A sensor without a 443 nm band falls back to the turbid branch
 * everywhere, which is what the reference does for Landsat 5.
 */

/**
 * @constant SPM_DEFAULTS
 * @description Coefficients and switches, kept identical to the reference script so a value changed
 * here is a deliberate departure from it and not a transcription drift.
 * @memberof module:Bathymetry
 */
export const SPM_DEFAULTS = {
  useSwirBaseline: true,
  re740A: 0.6,
  re740B: 0.4,
  clearBranch: 'AUTO',
  rrsSwitchLow: 0.0008,
  rrsSwitchHigh: 0.0012,
  turbidA: 20.43,
  turbidB: 2.15,
  turbidAlpha: 0.04,
  turbidBeta: [1.17, 0.4, 14.86],
  clearCoefs: [0.5192, 0.9278, 0.4291],
  min: 0.01,
  max: 500.0,
  epsilon: 1e-5,
};

/**
 * @constant SPM_BAND
 * @description Name of the band this module writes.
 * @memberof module:Bathymetry
 */
export const SPM_BAND = 'SPM02';

/**
 * @constant SPM_TRAINING_LIMIT
 * @description Suspended matter above which a pixel is kept out of a calibration fit, in mg/L.
 * The reference raised this from the value CASSIE used because the old one was measured on the
 * previous, incorrect SPM scale and does not transfer.
 * @memberof module:Bathymetry
 */
export const SPM_TRAINING_LIMIT = 30;

/**
 * @constant SPM_SHALLOW_EXEMPTION_M
 * @description Depth below which a pixel is kept regardless of its suspended matter, in metres.
 * A bright shallow bottom raises the NIR-RGB estimate without the water being turbid, so without
 * this exemption the filter would throw away exactly the shallow points a depth model needs most.
 * @memberof module:Bathymetry
 */
export const SPM_SHALLOW_EXEMPTION_M = 3.0;

/**
 * @function spmTrainingFilter
 * @description Keeps calibration points in clear water, plus every shallow point whatever its
 * suspended matter reads.
 * @param {String} [depthField] - Column holding the surveyed depth, which the shallow exemption is
 *  read from.
 * **Called by:
 * @see {ProcessingPage} - filtering the samples the analytic models are fitted on.
 * @memberof module:Bathymetry
 * @returns {Object} An `ee.Filter`.
 */
export const spmTrainingFilter = (depthField = 'or_depth') =>
  ee.Filter.or(ee.Filter.lt(SPM_BAND, SPM_TRAINING_LIMIT), ee.Filter.lte(depthField, SPM_SHALLOW_EXEMPTION_M));

/**
 * @function hasClearBranchBands
 * @description Whether the mission carries the 443 nm band the clear-water branch needs. Landsat 5
 * does not, and the reference falls back to the turbid formulation in every regime there.
 * @param {Object} missionBands - The mission's band map, as `missionInfo.bands`.
 * **Called by:
 * @see {addSPM02}
 * @memberof module:Bathymetry
 * @returns {Boolean}
 */
export const hasClearBranchBands = (missionBands) => Boolean(missionBands?.aer);

/**
 * @function hasSwirBaselineBands
 * @description Whether both short-wave infrared bands exist for the baseline subtraction.
 * @param {Object} missionBands - The mission's band map.
 * **Called by:
 * @see {addSPM02}
 * @memberof module:Bathymetry
 * @returns {Boolean}
 */
export const hasSwirBaselineBands = (missionBands) => Boolean(missionBands?.swir && missionBands?.swir2);

/**
 * @function addSPM02
 * @description Adds the `SPM02` band, in mg/L, to an image.
 * The algorithm blends two regimes. The turbid branch is a ratio of weighted near-infrared
 * reflectances over green, which is why it survives without absolute calibration; the clear branch
 * is a polynomial in the logarithm of green over the 443 nm band. The weight between them comes
 * from the red reflectance itself, so a pixel is read by the branch that suits its water.
 * Landsat has no 740 nm band, so it is synthesised as a linear combination of 665 and 865 nm, with
 * the coefficients the reference fitted for this purpose.
 * @param {Object} image - The working image, as an `ee.Image`.
 * @param {Object} missionBands - The mission's band map, as `missionInfo.bands`.
 * @param {Object} [options] - Overrides for {@link SPM_DEFAULTS}.
 * **Called by:
 * @see {predictorImage} - as one of the tree models' features.
 * @see {ProcessingPage} - as a column of the per-pixel CSV.
 * @memberof module:Bathymetry
 * @returns {Object} The image with `SPM02` added.
 */
export const addSPM02 = (image, missionBands, options = {}) => {
  const config = { ...SPM_DEFAULTS, ...options };
  const eps = config.epsilon;
  const source = ee.Image(image);

  const blue = source.select(missionBands.blue);
  const green = source.select(missionBands.green);
  const red = source.select(missionBands.red);
  const nir = source.select(missionBands.nir);

  /**
   * The per-pixel floor of the two short-wave infrared bands stands in for the atmospheric and
   * surface-reflected signal; each band falls back to the other where one is masked, as the
   * reference's `fmin` does.
   */
  let baseline = ee.Image.constant(0);
  if (config.useSwirBaseline && hasSwirBaselineBands(missionBands)) {
    const swir1 = source.select(missionBands.swir);
    const swir2 = source.select(missionBands.swir2);
    baseline = swir1.unmask(swir2).min(swir2.unmask(swir1)).max(0).unmask(0);
  }

  const bluePrime = blue.subtract(baseline);
  const greenPrime = green.subtract(baseline);
  const redPrime = red.subtract(baseline).max(0);
  const nirPrime = nir.subtract(baseline).max(0);
  const rededgePrime = redPrime.multiply(config.re740A).add(nirPrime.multiply(config.re740B)).max(0);

  const valid = bluePrime.gt(eps).and(greenPrime.gt(eps));

  const total = redPrime.add(rededgePrime).add(nirPrime).max(eps);
  const weighted = redPrime
    .divide(total)
    .multiply(redPrime)
    .multiply(config.turbidBeta[0])
    .add(rededgePrime.divide(total).multiply(rededgePrime).multiply(config.turbidBeta[1]))
    .add(nirPrime.divide(total).multiply(nirPrime).multiply(config.turbidBeta[2]));
  const index = greenPrime.divide(bluePrime).multiply(config.turbidAlpha).add(weighted.divide(greenPrime));
  const turbid = index.max(0).pow(config.turbidB).multiply(config.turbidA);

  let clear = turbid;
  if (config.clearBranch === 'AUTO' && hasClearBranchBands(missionBands)) {
    const aerosol = source.select(missionBands.aer);
    const aerosolPrime = aerosol.subtract(baseline);
    const usable = aerosol.gt(eps).and(aerosolPrime.gt(0));
    const logRatio = greenPrime.divide(aerosolPrime).updateMask(usable).log10();
    clear = logRatio
      .pow(2)
      .multiply(config.clearCoefs[2])
      .add(logRatio.multiply(config.clearCoefs[1]))
      .add(config.clearCoefs[0])
      .unmask(turbid);
  }

  const reflectance665 = redPrime.divide(Math.PI);
  const weight = reflectance665
    .subtract(config.rrsSwitchLow)
    .divide(config.rrsSwitchHigh - config.rrsSwitchLow)
    .clamp(0, 1);

  const blended = turbid.multiply(weight).add(clear.multiply(ee.Image.constant(1).subtract(weight)));
  const finite = blended.abs().lt(1e30);

  return source.addBands(
    blended.updateMask(valid.and(finite)).clamp(config.min, config.max).rename(SPM_BAND).toFloat(),
    null,
    true,
  );
};
